import { useMemo } from "react";

import type { Member, Settlement } from "../types";

export interface SettlementTransfer {
  from: string;
  to: string;
  amount: number;
}

/**
 * Compute per-member net balances and pairwise transfers for a settlement.
 *
 * `balances`: Map<userId, number>. Positive = the member is owed money;
 * negative = the member owes money.
 *
 * `transfers`: greedy pairing of largest creditor → largest debtor so each
 * member needs to make at most `min(members - 1, members - 1)` actual
 * payments rather than one per shared expense.
 *
 * When `ownerCoversInvoice` is true on the household, the owner is
 * treated as the implicit payer for every expense — see
 * `apps/api/src/db/queries/households.ts` for how that household-level
 * setting is enforced server-side. The frontend must mirror that math
 * here so the UI matches what the resolved transfers will actually be.
 */
export function useSettlementBalances(
  settlement: Settlement,
  members: Member[],
  ownerCoversInvoice: boolean,
): {
  balances: Map<string, number>;
  transfers: SettlementTransfer[];
} {
  const balances = useMemo(() => {
    const owedTo = new Map<string, number>();
    const ownerId = settlement.createdBy;
    for (const e of settlement.expenses) {
      const total = Number.parseFloat(e.amount);
      const payerId = ownerCoversInvoice ? ownerId : e.paidByUserId;
      const payerShare = Number.parseFloat(
        e.splits.find((s) => s.userId === payerId)?.share ?? "0",
      );
      owedTo.set(payerId, (owedTo.get(payerId) ?? 0) + (total - payerShare));
      for (const s of e.splits) {
        if (s.userId !== payerId) {
          owedTo.set(s.userId, (owedTo.get(s.userId) ?? 0) - Number.parseFloat(s.share));
        }
      }
    }
    return owedTo;
  }, [settlement.expenses, settlement.createdBy, ownerCoversInvoice]);

  // Flatten balances into pairwise transfers (greedy: largest creditor → largest debtor).
  const transfers = useMemo(() => {
    const result: SettlementTransfer[] = [];
    const creditors = members
      .map((m) => ({ userId: m.userId, balance: balances.get(m.userId) ?? 0 }))
      .filter((b) => b.balance > 0.01)
      .sort((a, b) => b.balance - a.balance);
    const debtors = members
      .map((m) => ({ userId: m.userId, balance: -(balances.get(m.userId) ?? 0) }))
      .filter((b) => b.balance > 0.01)
      .sort((a, b) => b.balance - a.balance);

    while (creditors.length > 0 && debtors.length > 0) {
      const c = creditors[0];
      const d = debtors[0];
      if (!c || !d) break;
      const amount = Math.min(c.balance, d.balance);
      result.push({ from: d.userId, to: c.userId, amount });
      c.balance -= amount;
      d.balance -= amount;
      if (c.balance < 0.01) creditors.shift();
      if (d.balance < 0.01) debtors.shift();
    }
    return result;
  }, [balances, members]);

  return { balances, transfers };
}
